/**
 * Organization social handles — the pure half of the Social Accounts card and
 * the profile's social-link row.
 *
 * An org admin types either a bare handle (`@acmerealty`, `acmerealty`) or a
 * pasted profile URL (`https://www.instagram.com/acmerealty/`). Both are reduced
 * here to one stored shape: a display handle and a canonical profile URL, which
 * `SocialAccountsSection` writes to `org_social_connections.platform_handle` and
 * `.profile_url`. The public profile then links straight to `profile_url`, so a
 * bad parse here is a broken outbound link there — which is why this is a pure
 * module with its own test rather than inline component code.
 *
 * The platform list mirrors the table's CHECK constraint
 * (`platform IN ('instagram','facebook','linkedin','youtube','x')`); adding a
 * platform here without a migration would fail at insert time.
 */

export type SocialPlatform = "instagram" | "facebook" | "linkedin" | "youtube" | "x";

export interface SocialPlatformSpec {
  key: SocialPlatform;
  label: string;
  /** Hosts a pasted URL may use for this platform (www. is stripped first). */
  hosts: string[];
  /** Placeholder shown in the editor input. */
  placeholder: string;
  /** Whether the display handle carries a leading `@`. */
  atPrefixed: boolean;
  /** Canonical profile URL for a bare handle. */
  urlForHandle: (handle: string) => string;
}

export const SOCIAL_PLATFORMS: SocialPlatformSpec[] = [
  {
    key: "instagram",
    label: "Instagram",
    hosts: ["instagram.com"],
    placeholder: "@yourbrokerage or instagram.com/yourbrokerage",
    atPrefixed: true,
    urlForHandle: (h) => `https://www.instagram.com/${h}`,
  },
  {
    key: "facebook",
    label: "Facebook",
    hosts: ["facebook.com", "fb.com"],
    placeholder: "yourbrokerage or facebook.com/yourbrokerage",
    atPrefixed: false,
    urlForHandle: (h) => `https://www.facebook.com/${h}`,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    hosts: ["linkedin.com"],
    placeholder: "linkedin.com/company/yourbrokerage",
    atPrefixed: false,
    urlForHandle: (h) => `https://www.linkedin.com/company/${h}`,
  },
  {
    key: "youtube",
    label: "YouTube",
    hosts: ["youtube.com", "youtu.be"],
    placeholder: "@yourbrokerage or youtube.com/@yourbrokerage",
    atPrefixed: true,
    urlForHandle: (h) => `https://www.youtube.com/@${h}`,
  },
  {
    key: "x",
    label: "X (Twitter)",
    hosts: ["x.com", "twitter.com"],
    placeholder: "@yourbrokerage or x.com/yourbrokerage",
    atPrefixed: true,
    urlForHandle: (h) => `https://x.com/${h}`,
  },
];

export const SOCIAL_PLATFORM_KEYS = SOCIAL_PLATFORMS.map((p) => p.key);

export const isSocialPlatform = (v: unknown): v is SocialPlatform =>
  typeof v === "string" && (SOCIAL_PLATFORM_KEYS as string[]).includes(v);

export const getSocialPlatform = (key: SocialPlatform): SocialPlatformSpec =>
  SOCIAL_PLATFORMS.find((p) => p.key === key)!;

/** What gets stored: a display handle and the URL the profile links to. */
export interface NormalizedSocialHandle {
  handle: string;
  profileUrl: string;
}

/**
 * Not a discriminated union on purpose: this repository compiles with
 * `strictNullChecks: false`, under which TypeScript does not narrow on a
 * boolean-literal discriminant, so `if (!r.ok) r.error` fails to typecheck.
 * `value` is present exactly when `ok` is true; `error` exactly when false.
 */
export interface NormalizeSocialResult {
  ok: boolean;
  value?: NormalizedSocialHandle;
  error?: string;
}

/** Characters the platforms themselves permit in a handle or page slug. */
const HANDLE_RE = /^[A-Za-z0-9._-]{1,100}$/;

/** Path prefixes under which LinkedIn nests a page. */
const LINKEDIN_PREFIXES = ["company", "in", "school", "showcase"];

const stripAt = (s: string) => s.replace(/^@+/, "");

const withAt = (spec: SocialPlatformSpec, handle: string) =>
  spec.atPrefixed ? `@${handle}` : handle;

const invalidHandle = (spec: SocialPlatformSpec): NormalizeSocialResult => ({
  ok: false,
  error: `Enter a valid ${spec.label} handle or profile link.`,
});

/**
 * YouTube is the one platform whose stored value has to be RESOLVABLE, not just
 * linkable. `resolve-youtube-channel` looks the value up through
 * `channels?forHandle=`, which answers for an @handle and for nothing else, so
 * a `/channel/<id>`, `/c/<name>` or `/user/<name>` URL cannot be resolved and
 * must not be quietly reinterpreted as one. The admin is told what to enter
 * instead of being handed a generic rejection.
 *
 * Rows saved before this narrowing are untouched and keep rendering: the public
 * link list reads their stored `profile_url` and never re-normalizes it. What
 * changes is only what may be entered from now on.
 */
const invalidYoutubeHandle = (): NormalizeSocialResult => ({
  ok: false,
  error: "Enter your YouTube channel's @handle — for example @yourbrokerage. A /channel/, /c/ or /user/ link can't be checked.",
});

/**
 * The rejection for one platform. YouTube always gets the @handle guidance,
 * from every rejecting path, so an admin is never told only that their input
 * was invalid when the actionable thing to say is which shape to use.
 */
const rejectHandle = (platform: SocialPlatform, spec: SocialPlatformSpec): NormalizeSocialResult =>
  platform === "youtube" ? invalidYoutubeHandle() : invalidHandle(spec);

/** Percent-decode every path segment; null when any segment is malformed. */
function safeDecodeSegments(pathname: string): string[] | null {
  try {
    return pathname.split("/").filter(Boolean).map(decodeURIComponent);
  } catch {
    return null;
  }
}

/** Try to read the input as a URL; returns null when it clearly is not one. */
function parseAsUrl(raw: string): URL | null {
  const looksLikeUrl = /^[a-z]+:\/\//i.test(raw) || /^(www\.)?[a-z0-9-]+\.[a-z]{2,}(\/|$)/i.test(raw);
  if (!looksLikeUrl) return null;
  const candidate = /^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * Reduce user input for one platform to `{ handle, profileUrl }`.
 *
 * Accepts a bare handle with or without `@`, or a profile URL on one of the
 * platform's own hosts. A URL on a different platform's host is rejected with a
 * message naming the mismatch, because silently storing it would put an
 * Instagram link behind an X icon on the public profile.
 */
export function normalizeSocialHandle(platform: SocialPlatform, rawInput: string): NormalizeSocialResult {
  const spec = getSocialPlatform(platform);
  const raw = (rawInput ?? "").trim();
  if (!raw) return { ok: false, error: "Enter a handle or profile link." };

  const url = parseAsUrl(raw);

  if (!url) {
    const handle = stripAt(raw);
    if (!HANDLE_RE.test(handle)) return rejectHandle(platform, spec);
    return { ok: true, value: { handle: withAt(spec, handle), profileUrl: spec.urlForHandle(handle) } };
  }

  const host = url.hostname.toLowerCase().replace(/^(www|m|mobile)\./, "");
  if (!spec.hosts.includes(host)) {
    const other = SOCIAL_PLATFORMS.find((p) => p.hosts.includes(host));
    return {
      ok: false,
      error: other
        ? `That looks like a ${other.label} link — paste it in the ${other.label} field instead.`
        : `That link isn't on ${spec.label}. Paste your ${spec.label} profile link or handle.`,
    };
  }

  // decodeURIComponent throws a URIError on a truncated or malformed escape
  // ("%E0%A4%A"). Callers run this normalizer outside any try (the save
  // handler validates before it enters its own), so a throw here would escape
  // the click handler. A malformed path is simply not a valid profile link.
  const segments = safeDecodeSegments(url.pathname);
  if (!segments) return rejectHandle(platform, spec);
  if (segments.length === 0) return rejectHandle(platform, spec);

  // Platform-specific path shapes. Everything else is `<host>/<handle>`.
  switch (platform) {
    case "linkedin": {
      const [prefix, slug] = segments;
      if (!LINKEDIN_PREFIXES.includes(prefix) || !slug || !HANDLE_RE.test(slug)) return invalidHandle(spec);
      return {
        ok: true,
        value: { handle: slug, profileUrl: `https://www.linkedin.com/${prefix}/${slug}` },
      };
    }
    case "youtube": {
      // An @handle path and nothing else. See `invalidYoutubeHandle`.
      const [first] = segments;
      if (!first.startsWith("@")) return invalidYoutubeHandle();
      const handle = stripAt(first);
      if (!HANDLE_RE.test(handle)) return invalidYoutubeHandle();
      return { ok: true, value: { handle: `@${handle}`, profileUrl: spec.urlForHandle(handle) } };
    }
    case "facebook": {
      if (segments[0] === "pages" || segments[0] === "people") {
        // /pages/<Name>/<id> and /people/<Name>/<id> — keep the URL, display the name.
        const name = segments[1];
        const id = segments[2];
        if (!name || !id) return invalidHandle(spec);
        return { ok: true, value: { handle: name, profileUrl: `https://www.facebook.com/${segments[0]}/${encodeURIComponent(name)}/${id}` } };
      }
      if (segments[0] === "profile.php") return invalidHandle(spec);
      const handle = stripAt(segments[0]);
      if (!HANDLE_RE.test(handle)) return invalidHandle(spec);
      return { ok: true, value: { handle, profileUrl: spec.urlForHandle(handle) } };
    }
    case "instagram":
    case "x": {
      const handle = stripAt(segments[0]);
      if (!HANDLE_RE.test(handle)) return invalidHandle(spec);
      return { ok: true, value: { handle: withAt(spec, handle), profileUrl: spec.urlForHandle(handle) } };
    }
  }
}

/** A stored connection as the public RPC / admin read returns it. */
export interface StoredSocialConnection {
  platform: string;
  platform_handle: string | null;
  profile_url: string | null;
}

export interface OrgSocialLink {
  platform: SocialPlatform;
  label: string;
  handle: string;
  url: string;
}

/**
 * Turn stored rows into the ordered, renderable link list. Rows on unknown
 * platforms, or with neither a handle nor a URL, are dropped — nothing is
 * rendered for a platform the org did not fill in. A row that predates this
 * module and carries only a handle gets its URL derived, so legacy X rows still
 * link.
 */
export function toOrgSocialLinks(rows: StoredSocialConnection[] | null | undefined): OrgSocialLink[] {
  if (!rows?.length) return [];
  const byPlatform = new Map<SocialPlatform, OrgSocialLink>();
  for (const row of rows) {
    const platform = row.platform === "twitter" ? "x" : row.platform;
    if (!isSocialPlatform(platform)) continue;
    const spec = getSocialPlatform(platform);
    const rawHandle = (row.platform_handle ?? "").trim();
    const bare = stripAt(rawHandle);
    const url = row.profile_url?.trim() || (bare && HANDLE_RE.test(bare) ? spec.urlForHandle(bare) : "");
    if (!url) continue;
    const handle = rawHandle ? withAt(spec, bare) : spec.label;
    byPlatform.set(platform, { platform, label: spec.label, handle, url });
  }
  return SOCIAL_PLATFORMS.filter((p) => byPlatform.has(p.key)).map((p) => byPlatform.get(p.key)!);
}
